import { useOktaAuth } from "@okta/okta-react";
import { Link } from "@tanstack/react-router";
import { AxleLogoColor, MdLogout, MdRoundOpenInNew } from "@teamworksdev/icons";
import { Button, cn } from "@teamworksdev/react";
import type { ComponentProps } from "react";
import config from "~/config";
import { usePartialMatchCountsQuery } from "~/modules/PartialMatches/queries";

const isSupportToolsEnabled = import.meta.env.VITE_SUPPORT_TOOLS_ENABLED === "true";

const MainNav = () => {
  const { oktaAuth } = useOktaAuth();

  const logout = async () => {
    await oktaAuth.signOut();
    // You can perform any additional cleanup or redirect here
  };

  const { data: partialMatchCountTotal } = usePartialMatchCountsQuery({
    params: { by: "org" },
    select: (data) => Object.values(data).reduce((acc, v) => acc + v, 0),
  });

  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex">
        <Link className="mr-6 flex items-center space-x-2" to="/">
          <AxleLogoColor height={30} width={30} />
          <span className="hidden font-bold sm:inline-block">{config.siteName}</span>
        </Link>
        <nav className="flex items-center h-14 space-x-6 text-sm font-medium">
          <NavLink to="/orgs">Organizations </NavLink>
          <NavLink to="/partial-matches-by-app">
            Partial matches by app {!!partialMatchCountTotal && <Indicator />}
          </NavLink>
          {isSupportToolsEnabled && <NavLink to="/support-tools">Support tools</NavLink>}
        </nav>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" leftIcon={<MdRoundOpenInNew />} asChild>
          <a
            href="https://www.notion.so/teamworksoss/Teamworks-User-Management-b22ec707b60a4f34a717b9c7883cc560"
            target="_blank"
            rel="noopener noreferrer"
          >
            Documentation
          </a>
        </Button>
        <Button leftIcon={<MdLogout />} onClick={logout}>
          Log out
        </Button>
      </div>
    </div>
  );
};

const NavLink = ({ children, className, ...props }: ComponentProps<typeof Link>) => {
  return (
    <Link
      activeProps={{ className: "!text-black" }}
      className={cn(
        "text-black/50 transition-colors hover:text-black/80",
        "flex flex-row gap-x-0.5 items-center relative h-full",
        className,
      )}
      {...props}
    >
      {children}
    </Link>
  );
};

const Indicator = () => (
  <div className="absolute -right-2 top-1/4">
    <div className="w-2 h-2 rounded-full bg-warning-700" />
  </div>
);

export default MainNav;
